// Messages types for React Native admin app
// Based on admin-web messages implementation

// Lease and Customer related interfaces
export interface ILease {
  id: number;
  statusType: string;
  unitNumber?: string;
  unitNumberSpace?: string;
}

export interface ILeaseCustomer {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  customer_type?: 'primary' | 'guarantor';
  customerType?: 'primary' | 'guarantor';
  lease?: ILease;
  lease_id?: number;
  account?: IAccount;
  accountId?: number;
}

export interface IRole {
  id: number;
  name: string;
  organizationId?: number;
  propertyId?: number;
}

export interface IAccount {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  ConversationAccount?: IConversationAccount;
  leaseCustomers?: ILeaseCustomer[]; // Properly typed lease customers
  lastInteractionDate?: string; // For checking if user has logged in
  roles?: IRole[]; // Properly typed admin roles
}

export interface IConversationAccount {
  assignedAt: string | null;
  assigneeAccount: IAccount;
  conversationId: number;
  id: number;
  type: string;
  assignedBy: IAccount;
}

export interface IFile {
  id?: number;
  file?: File | any; // For mobile, this could be different
  fileUrl?: string;
  label: string;
  name?: string;
  size?: number;
  type?: string;
  uri?: string; // For React Native file handling
}

export interface IMessage {
  content: string;
  files: IFile[];
  id: number;
  isAdmin: boolean;
  sender: IAccount;
  createdAt: string;
}

export interface IConversation {
  id: number;
  accounts?: IAccount[];
  archived: boolean;
  messages: IMessage[];
  createdAt: string;
  subject: string;
  sendEmail: boolean;
  conversationAccounts?: IConversationAccount[];
}

// API Response interfaces
export interface IConversationListResponse {
  success: boolean;
  response: IConversation[];
}

export interface IMessageResidentsResponse {
  success: boolean;
  response: IMessageResident[];
}

export interface IMessageResident {
  id: number;
  statusType: string;
  unitNumber?: string;
  unitNumberSpace?: string;
  isUnit?: boolean; // For unit-level recipients
  accounts?: IMessageResident[]; // For unit accounts
  leaseCustomers: ILeaseCustomer[];
}

export interface IApiResponse<T = any> {
  success: boolean;
  response: T;
  message?: string;
}

// Filter and sort types
export const FILTER_VALUES = {
  open: 'Open',
  archived: 'Archived',
  assigned: 'Inbox',
} as const;

export const SORT_VALUES = {
  latest: 'Sort by latest',
  aToZ: 'Sort by subject (a-z)',
  zToA: 'Sort by subject (z-a)',
} as const;

export type FilterType = keyof typeof FILTER_VALUES;
export type SortType = keyof typeof SORT_VALUES;

// Form types
export interface ISendMessage {
  files: IFile[];
  messageContent: string;
  isAdmin: boolean;
}

export interface ICreateConversation {
  subject: string;
  content: string;
  sendEmail: boolean;
  residents: IAccount[];
  files: IFile[];
}

export interface IEditConversation {
  id: number;
  subject: string;
  sendEmail: boolean;
  residents: IAccount[];
}

// Component prop types
export interface IConversationListProps {
  conversationQuery?: string;
  selectedConversation?: number;
  setSelectedConversation: (id: number | undefined) => void;
}

export interface IConversationRowProps {
  conversation: IConversation;
  setSelectedConversation: (id: number | undefined) => void;
  isSelected: boolean;
  onPress?: () => void;
}

export interface IChatWindowProps {
  conversationId: number;
}

export interface IMessageBubbleProps {
  message: IMessage;
  isCurrentUser?: boolean;
}

export interface IFileBubbleProps {
  file: IFile;
  isAdmin: boolean;
  onPress?: () => void;
}

export interface IMessageFooterProps {
  conversationId: number;
  onMessageSent?: () => void;
}

// Mobile-specific types
export interface IMessagesScreenState {
  showCreateConversation: boolean;
  selectedConversation?: number;
  conversationQuery?: string;
  editConversation?: IConversation;
  newMessageContent?: string;
}

export interface IConversationCardProps {
  conversation: IConversation;
  onPress: () => void;
  isSelected?: boolean;
}

export interface IMessageCardProps {
  message: IMessage;
  isCurrentUser?: boolean;
  showSender?: boolean;
}

// API query parameters
export interface IConversationQueryParams {
  propertyId: string;
  pageNum: string;
  isAdmin: string;
  filterType: FilterType;
  sortType: SortType;
  searchQuery?: string;
}

export interface IMessageResidentsQueryParams {
  propertyId: string;
  searchQuery?: string;
}

// Error handling
export interface IMessagesError {
  message: string;
  code?: string;
  details?: any;
}

// Constants for mobile UI
export const MESSAGE_CONSTANTS = {
  MAX_SUBJECT_LENGTH: 30,
  MAX_MESSAGE_PREVIEW_LENGTH: 30,
  STALE_CONVERSATION_DAYS: 30,
  MAX_FILE_NAME_LENGTH: 17,
} as const;

// Mobile-specific layout constants
export const MOBILE_LAYOUT = {
  CONVERSATION_ITEM_HEIGHT: 80,
  MESSAGE_BUBBLE_MAX_WIDTH: '80%',
  AVATAR_SIZE: 40,
  FILE_PREVIEW_SIZE: 80,
} as const;
